import { Provider } from 'react-redux';
import { createStore, applyMiddleware, compose } from 'redux';

import { LocalisationProvider } from '../../services/contextAdaptor/LocalisationContext';

import MonthView from './components/MonthView/MonthView';
import { urlConstants } from './helpers/constants/urlConstants';
import gridEntryHelper from './helpers/data-parsing/gridEntryHelper';
import hotelsUrlBuilder from './helpers/interface-handling/hotelsUrlBuilder';
import initialise from './helpers/interface-handling/initialisation';
import { analyticsMiddleware } from './middleware/analytics';
import { navigationMiddleware } from './middleware/navigation';
import { urlUpdating } from './middleware/urlUpdating';
import monthView from './reducers';
import { getLocalisationVars, getPriceSourceId, isReturn } from './selectors';
import getSelectedDaysPrice from './selectors/selectedDaysPrice';

import type { State } from './selectors/types';
import type { MonthViewContext } from '../../services/contextAdaptor/types';
import type { Middleware, Store } from 'redux';

const createApplication = (
  context: MonthViewContext,
  preloadedState: Partial<State> = {},
) => {
  const middlewareWithCallbacks: Middleware = (store) =>
    analyticsMiddleware(
      // Redux middleware receives `MiddlewareAPI`, but `analyticsMiddleware` is still typed to accept `Store`.
      // Runtime is safe as we only access store.getState which is also available on MiddlewareAPI;
      store as Store,
      context.callbacks.logGaEvent,
      context.device,
    );

  const navigationMiddlewareWithCallbacks: Middleware = (store) => {
    const isMobilePhone = context.device?.isMobilePhone ?? false;
    const onResultConfirmed = (
      outboundDate: Date,
      inboundDate: Date,
      preferDirects: boolean,
      departureTimeRange?: string,
    ) => {
      let prevPrice: string | number = 0;
      const state = store.getState();
      const price = getSelectedDaysPrice(state);
      if (price) {
        prevPrice = price;
      } else if (isReturn(state)) {
        prevPrice = urlConstants.RETURN_NO_PRICE;
      } else {
        prevPrice = urlConstants.ONE_WAY_NO_PRICE;
      }
      const prevProvider = urlConstants.INS_MONTH;
      const prevCurrency = getLocalisationVars(state).currency;
      const priceSourceId = getPriceSourceId(state);
      const summaryData = gridEntryHelper.getSelectedSummaryData(state);
      const priceTrace = summaryData.trace ?? '';
      const { stay } = context;

      context.callbacks.navigateToResultUrl(
        outboundDate,
        inboundDate,
        preferDirects,
        stay,
        prevProvider,
        prevCurrency,
        prevPrice,
        priceSourceId,
        priceTrace,
        !isMobilePhone,
        departureTimeRange,
      );
    };
    const onHotelConfirmed = (
      checkinDate: Date,
      checkoutDate: Date,
      destinationId: string,
      impressionId: string,
    ) => {
      const stay = {
        adults: context.stay.adults ?? null,
        childrenAges: context.stay.childrenAges ?? [],
      };
      const url = hotelsUrlBuilder.buildDayviewUrl({
        checkinDate,
        checkoutDate,
        skyId: destinationId,
        stay,
        impressionId,
      });

      context.callbacks.navigateToUrl(url, !isMobilePhone);
    };
    return navigationMiddleware(store, onResultConfirmed, onHotelConfirmed);
  };

  const urlUpdatingMiddlewareWithCallbacks: Middleware = (store) =>
    urlUpdating(store as Store, context.callbacks.inputParamChanged);

  const middleware: Middleware[] = [
    middlewareWithCallbacks,
    navigationMiddlewareWithCallbacks,
    urlUpdatingMiddlewareWithCallbacks,
  ];
  const composeEnhancers =
    // eslint-disable-next-line no-underscore-dangle
    window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
  const store = createStore(
    monthView,
    preloadedState,
    composeEnhancers(applyMiddleware(...middleware)),
  );

  initialise(store, context);
  return (
    <Provider store={store}>
      <LocalisationProvider value={context.localisation}>
        <MonthView callbacks={context.callbacks} />
      </LocalisationProvider>
    </Provider>
  );
};

export default createApplication;
